import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postData } from "../../../utility/Utility";
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";
import moment from "moment";

const FollowupLeadHistory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const lead = location.state?.lead || null;
  const leadId = lead?._id || "";

  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchHistory = async () => {
    if (!leadId) {
      toast.error("Lead information not found");
      return;
    }

    setLoading(true);
    try {
      const response = await postData("api/lead-work-history/lead", {
        leadId,
      });

      if (response?.success) {
        setAgents(response.data?.agents || []);
      } else {
        toast.error(response?.message || "Failed to fetch follow-up history");
      }
    } catch (error) {
      toast.error("Error fetching follow-up history");
      console.error("Fetch follow-up history error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const formatDate = (dateString) => {
    if (!dateString) return "Time not available";
    return moment(dateString).format("MMM DD, YYYY [at] hh:mm A");
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return "";
    return moment(dateString).fromNow();
  };

  const isRecentActivity = (dateString) => {
    if (!dateString) return false;
    return moment().diff(moment(dateString), "hours") < 24;
  };

  return (
    <div className="content-page">
      <ToastContainer position="top-right" autoClose={3000} />
      <CustomLoader loading={loading} />

      <div className="content">
        <div className="container-fluid">
          {/* Header Section */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="page-title-box d-flex align-items-center justify-content-between">
                <div className="flex-grow-1 mt-3">
                  <h4 className="fs-20 fw-semibold text-dark mb-1">
                    Follow-up History
                  </h4>
                  <p className="text-muted mb-0">
                    Tracking all agent interactions for Lead ID:{" "}
                    {lead?.fullName ? ` - ${lead.fullName}` : ""}
                  </p>
                </div>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary d-flex align-items-center"
                    onClick={() => navigate(-1)}
                  >
                    <i className="ri-arrow-left-line me-2"></i>
                    Back
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary d-flex align-items-center"
                    onClick={fetchHistory}
                    disabled={loading}
                  >
                    <i
                      className={`ri-refresh-line me-2 ${
                        loading ? "spin" : ""
                      }`}
                    ></i>
                    {loading ? "Refreshing..." : "Refresh"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Lead Information Card */}
          {lead && (
            <div className="row mb-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-transparent border-bottom py-3">
                    <h5 className="card-title mb-0 d-flex align-items-center">
                      <i className="ri-user-line me-2 text-primary"></i>
                      Lead Details
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="row g-4">
                      <div className="col-xl-3 col-md-6">
                        <div className="d-flex align-items-center">
                          <div className="flex-shrink-0">
                            <div className="avatar-sm rounded-circle bg-soft-primary d-flex align-items-center justify-content-center">
                              <i className="ri-user-3-line fs-18 text-primary"></i>
                            </div>
                          </div>
                          <div className="flex-grow-1 ms-3">
                            <h6 className="mb-1 fs-14 text-muted">Lead Name</h6>
                            <p className="mb-0 fw-semibold text-dark">
                              {lead.fullName || "N/A"}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="col-xl-3 col-md-6">
                        <div className="d-flex align-items-center">
                          <div className="flex-shrink-0">
                            <div className="avatar-sm rounded-circle bg-soft-success d-flex align-items-center justify-content-center">
                              <i className="ri-phone-line fs-18 text-success"></i>
                            </div>
                          </div>
                          <div className="flex-grow-1 ms-3">
                            <h6 className="mb-1 fs-14 text-muted">
                              Contact Number
                            </h6>
                            <p className="mb-0 fw-semibold text-dark">
                              {lead.phoneNumber || "N/A"}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="col-xl-3 col-md-6">
                        <div className="d-flex align-items-center">
                          <div className="flex-shrink-0">
                            <div className="avatar-sm rounded-circle bg-soft-warning d-flex align-items-center justify-content-center">
                              <i className="ri-mail-line fs-18 text-warning"></i>
                            </div>
                          </div>
                          <div className="flex-grow-1 ms-3">
                            <h6 className="mb-1 fs-14 text-muted">
                              Email Address
                            </h6>
                            <p className="mb-0 fw-semibold text-dark">
                              {lead.email || "N/A"}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="col-xl-3 col-md-6">
                        <div className="d-flex align-items-center">
                          <div className="flex-shrink-0">
                            <div className="avatar-sm rounded-circle bg-soft-info d-flex align-items-center justify-content-center">
                              <i className="ri-question-line fs-18 text-info"></i>
                            </div>
                          </div>
                          <div className="flex-grow-1 ms-3">
                            <h6 className="mb-1 fs-14 text-muted">
                              Enquiry Type
                            </h6>
                            <p className="mb-0 fw-semibold text-dark">
                              {lead.enquiryType || "N/A"}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="col-xl-3 col-md-6">
                        <div className="d-flex align-items-center">
                          <div className="flex-shrink-0">
                            <div className="avatar-sm rounded-circle bg-soft-purple d-flex align-items-center justify-content-center">
                              <i className="ri-map-pin-line fs-18 text-purple"></i>
                            </div>
                          </div>
                          <div className="flex-grow-1 ms-3">
                            <h6 className="mb-1 fs-14 text-muted">Location</h6>
                            <p className="mb-0 fw-semibold text-dark">
                              {lead.location?.city || ""}
                              {lead.location?.city && lead.location?.state
                                ? ", "
                                : ""}
                              {lead.location?.state || "N/A"}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="col-xl-3 col-md-6">
                        <div className="d-flex align-items-center">
                          <div className="flex-shrink-0">
                            <div className="avatar-sm rounded-circle bg-soft-danger d-flex align-items-center justify-content-center">
                              <i className="ri-calendar-2-line fs-18 text-danger"></i>
                            </div>
                          </div>
                          <div className="flex-grow-1 ms-3">
                            <h6 className="mb-1 fs-14 text-muted">
                              Lead Generated
                            </h6>
                            <p className="mb-0 fw-semibold text-dark">
                              {lead.createdAt
                                ? moment(lead.createdAt).format("MMM DD, YYYY")
                                : "N/A"}
                            </p>
                            <small className="text-muted">
                              {lead.createdAt &&
                                `(${moment(lead.createdAt).fromNow()})`}
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Follow-up History Section */}
          <div className="row">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-transparent border-bottom py-3">
                  <h5 className="card-title mb-0 d-flex align-items-center">
                    <i className="ri-history-line me-2 text-success"></i>
                    Agent Follow-up Timeline
                    <span className="badge bg-soft-primary text-primary ms-2">
                      {agents.length} {agents.length === 1 ? "Agent" : "Agents"}
                    </span>
                  </h5>
                </div>
                <div className="card-body">
                  {agents.length > 0 ? (
                    <div className="accordion" id="agentAccordion">
                      {agents.map((agent, agentIndex) => (
                        <div
                          className="accordion-item mb-3"
                          key={agent.user._id}
                        >
                          <h2
                            className="accordion-header"
                            id={`heading${agentIndex}`}
                          >
                            <button
                              className="accordion-button collapsed"
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target={`#collapse${agentIndex}`}
                              aria-expanded="false"
                              aria-controls={`collapse${agentIndex}`}
                            >
                              <div className="d-flex align-items-center w-100">
                                <div className="flex-shrink-0 me-3">
                                  {agent.user.image ? (
                                    <img
                                      src={agent.user.image}
                                      alt="Agent"
                                      className="rounded-circle avatar-xs"
                                    />
                                  ) : (
                                    <div className="avatar-xs rounded-circle bg-soft-primary d-flex align-items-center justify-content-center">
                                      <i className="ri-user-line fs-14 text-primary"></i>
                                    </div>
                                  )}
                                </div>
                                <div className="flex-grow-1">
                                  <h6 className="mb-0 fw-semibold">
                                    {agent.user.fullName}
                                  </h6>
                                  <small className="text-muted">
                                    {agent.user.email} | {agent.user.mobile}
                                  </small>
                                </div>
                                <div className="text-end">
                                  <span className="badge bg-soft-primary text-primary">
                                    {agent.interactions[0]?.followUps?.length ||
                                      0}{" "}
                                    Follow-ups
                                  </span>
                                </div>
                              </div>
                            </button>
                          </h2>
                          <div
                            id={`collapse${agentIndex}`}
                            className="accordion-collapse collapse"
                            aria-labelledby={`heading${agentIndex}`}
                            data-bs-parent="#agentAccordion"
                          >
                            <div className="accordion-body pt-0">
                              <div className="timeline">
                                {agent.interactions[0]?.followUps?.map(
                                  (followup, followupIndex) => (
                                    <div
                                      key={followup._id}
                                      className={`timeline-item ${
                                        isRecentActivity(followup.date)
                                          ? "timeline-item-recent"
                                          : ""
                                      }`}
                                    >
                                      <div className="timeline-line"></div>
                                      <div className="timeline-icon">
                                        <div className="avatar-xs rounded-circle bg-soft-primary d-flex align-items-center justify-content-center">
                                          <i className="ri-chat-1-line fs-14 text-primary"></i>
                                        </div>
                                      </div>
                                      <div className="timeline-content card border-0 shadow-none">
                                        <div className="card-body">
                                          <div className="d-flex justify-content-between align-items-start mb-2">
                                            <div className="d-flex align-items-center">
                                              <h6 className="mb-0 fw-semibold">
                                                Follow-up #{followupIndex + 1}
                                              </h6>
                                              {isRecentActivity(
                                                followup.date
                                              ) && (
                                                <span
                                                  className="badge bg-soft-success text-success ms-2"
                                                  style={{
                                                    whiteSpace: "nowrap",
                                                  }}
                                                >
                                                  <i className="ri-time-line me-1"></i>
                                                  Recent
                                                </span>
                                              )}
                                            </div>
                                            <div className="text-end">
                                              <small className="text-muted d-block">
                                                {formatDate(followup.date)}
                                              </small>
                                              <small className="text-primary fw-semibold">
                                                {formatRelativeTime(
                                                  followup.date
                                                )}
                                              </small>
                                            </div>
                                          </div>

                                          <div className="bg-light rounded p-3 mt-2">
                                            <small className="text-muted d-block mb-1">
                                              <i className="ri-chat-4-line me-1"></i>
                                              Agent Notes:
                                            </small>
                                            <p className="mb-0 fst-italic text-dark">
                                              "{followup.note}"
                                            </p>
                                          </div>

                                          {followup.nextFollowUpDate && (
                                            <div className="mt-2">
                                              <small className="text-muted d-block mb-1">
                                                <i className="ri-calendar-todo-line me-1"></i>
                                                Next Follow-up Scheduled:
                                              </small>
                                              <p className="mb-0 fw-semibold text-dark">
                                                {formatDate(
                                                  followup.nextFollowUpDate
                                                )}
                                              </p>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <div className="avatar-lg mx-auto mb-3">
                        <div className="avatar-title bg-soft-light text-light rounded-circle">
                          <i className="ri-inbox-line fs-24"></i>
                        </div>
                      </div>
                      <h5 className="text-muted">
                        No Follow-up Activity Found
                      </h5>
                      <p className="text-muted mb-4">
                        This lead hasn't been assigned to any agent for
                        follow-up yet.
                      </p>
                      <button
                        className="btn btn-primary"
                        onClick={() => navigate(-1)}
                      >
                        <i className="ri-arrow-left-line me-2"></i>
                        Back to Leads
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FollowupLeadHistory;
